import type { Command } from '@o2d/history';
import type { BoardState } from '../state/board-state.js';
import type { Move } from './move-objects.command.js';
import { isLine } from '../../features/line/line.types.js';
import { translateLine } from '../../features/line/line-translate.helper.js';

/** Internal, mutable twin of {@link Move} so merge() can grow the `to` target. */
interface MutableMove {
  readonly id: string;
  readonly from: { x: number; y: number };
  to: { x: number; y: number };
}

/**
 * Translate one or many objects by a fixed delta — the command behind
 * arrow-key nudging (1 document px, or 10 with Shift held).
 *
 * Carries absolute from/to positions per object (same `Move` shape as
 * {@link MoveObjectsCommand}) so it serializes onto the granular command
 * stream as a `move` and revert needs no delta bookkeeping. Consecutive
 * nudges in the SAME direction over the SAME selection coalesce via
 * {@link merge} into a single undo step — matching InDesign/Figma where a
 * burst of taps is one undo. A direction change or a different selection
 * breaks the run.
 */
export class NudgeObjectsCommand implements Command<BoardState> {
  readonly label: string;

  #moves: MutableMove[];

  constructor(
    moves: readonly Move[],
    /** Sign of this nudge's step on each axis — gates {@link merge}. */
    private readonly _stepX: number,
    private readonly _stepY: number,
  ) {
    this.#moves = moves.map((m) => ({ id: m.id, from: { ...m.from }, to: { ...m.to } }));
    this.label = this.#moves.length === 1 ? 'Objekt verschieben' : `${this.#moves.length} Objekte verschieben`;
  }

  get moves(): readonly Move[] {
    return this.#moves;
  }

  get stepX(): number {
    return this._stepX;
  }
  get stepY(): number {
    return this._stepY;
  }

  apply(state: BoardState): void {
    for (const m of this.#moves) this.#place(state, m.id, m.to, m.from);
  }

  revert(state: BoardState): void {
    for (const m of this.#moves) this.#place(state, m.id, m.from, m.to);
  }

  /**
   * Coalesce a follow-up nudge into this one when it targets the same
   * selection and pushes in the same direction. The accumulated command keeps
   * each object's original `from` but adopts the latest `to`, so one revert
   * undoes the whole burst. Returns `this`, or `null` to start a fresh step.
   */
  merge(next: Command<BoardState>): Command<BoardState> | null {
    if (!(next instanceof NudgeObjectsCommand)) return null;
    if (this._stepX !== next._stepX || this._stepY !== next._stepY) return null;
    if (!sameTargets(this.#moves, next.#moves)) return null;
    const nextById = new Map(next.#moves.map((m) => [m.id, m]));
    for (const m of this.#moves) {
      const n = nextById.get(m.id);
      if (n) m.to = { ...n.to };
    }
    return this;
  }

  #place(state: BoardState, id: string, to: { x: number; y: number }, from: { x: number; y: number }): void {
    const obj = state.getObject(id);
    if (!obj) return;
    if (isLine(obj)) {
      // Rigid line move: endpoints AND the curve controls translate together.
      // The box lands exactly on `to`, as before — it differs from `x + dx` only
      // when `from` is not the current box (merged nudge commands).
      state.updateObject({ ...translateLine(obj, to.x - from.x, to.y - from.y), x: to.x, y: to.y });
    } else {
      state.updateObject({ ...obj, x: to.x, y: to.y });
    }
  }
}

/** Two move sets target the same objects (order-independent). */
function sameTargets(a: readonly MutableMove[], b: readonly MutableMove[]): boolean {
  if (a.length !== b.length) return false;
  const idsB = new Set(b.map((m) => m.id));
  return a.every((m) => idsB.has(m.id));
}
